import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { debounce } from 'lodash/fp';
import { FC, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { Option } from 'dashboard/components/JsonSchemaSelect';
import SideInput from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/SideInput';
import {
  ConditionalRule,
  ConditionalRuleRightHand,
  ConditionalRuleValueAll,
  ConditionalRuleValueConfig,
  ConditionalRuleValueTypes,
  getConditionalRuleParts,
  getConditionalRuleValueConfig,
  getJsonSchemaType,
  getOperatorDisplayName,
  JsonLogicField,
  JsonLogicValue,
  RuleValueConfigField,
  RuleValueConfigValue,
  RuleValueTypes,
  snakify,
  valueTransformer,
} from 'dashboard/lib/workflows';
import fieldMetas from 'dashboard/lib/workflows/fieldMetas';
import {
  VERSION_OPERATORS,
  WorkflowFieldKey,
  WorkflowOperatorKey,
  WorkflowSchema,
} from 'dashboard/models/workflow';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import Select, { Option as SelectOption } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import { JsonSchemaTypeToFieldType } from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';
import { hasNoPossibilities, isMultiValue } from 'lib/workflows';

const OptionTypeSelect = styled(Select)`
  width: 96px;
  background-color: ${Color.CoreSlate100};
  border-color: ${Color.CoreSlate100};
`;

const DEFAULT_LEFT_CONFIG: RuleValueConfigField = {
  type: RuleValueTypes.Field,
  value: { var: '' },
  paths: [''],
};

const DEFAULT_RIGHT_CONFIG: RuleValueConfigValue = {
  type: RuleValueTypes.Value,
  value: '',
  paths: [],
};

const RIGHT_HAND_OPTIONS = [RuleValueTypes.Field, RuleValueTypes.Value].map((valueType) => ({
  value: valueType,
  label: valueType,
}));

const LEFT_HAND_OPTIONS = RIGHT_HAND_OPTIONS.filter(
  (option) => option.value !== RuleValueTypes.Value,
);

// TODO(@platform): Remove this filter once we support comparisons on complex objects
const SUPPORTED_COMPLEX_ARRAY_OPERATOR = [
  WorkflowOperatorKey.IsBlank,
  WorkflowOperatorKey.IsNotBlank,
  WorkflowOperatorKey.SizeGreaterThan,
  WorkflowOperatorKey.SizeGreaterThanOrEqual,
  WorkflowOperatorKey.SizeLessThan,
  WorkflowOperatorKey.SizeLessThanOrEqual,
  WorkflowOperatorKey.SizeEquals,
];

// TODO(@platform): Remove this filter once we support fine grained trigger types beyond string, bool, etc.
// We need this right now because we only want to show the version operators on the ClientMobileSdkVersion
// and ClientOsName criteria variables.
const SUPPORTED_WORKFLOW_FIELD_KEY_DESCRIPTIONS_FOR_VERSION_OPERATORS = [
  fieldMetas[WorkflowFieldKey.ClientMobileSdkVersion].description,
];

const buildValueOneSide = (type: ConditionalRuleValueTypes, value: JsonLogicValue) => {
  switch (type) {
    case RuleValueTypes.Field: {
      if (Array.isArray(value)) {
        return value.map((val) => ({
          var: snakify(val),
        }));
      }
      return { var: snakify(value as string) };
    }
    case RuleValueTypes.Value:
      return value;
    default:
      return assertNever(type);
  }
};

export const buildConditionalRule = (
  operator: WorkflowOperatorKey,
  leftRuleValue: JsonLogicField,
  rightRuleValue?: ConditionalRuleValueAll,
) => {
  const values: ConditionalRuleRightHand = [leftRuleValue];
  if (rightRuleValue != null) {
    values.push(rightRuleValue);
  }

  return {
    [operator]: values,
  };
};

const generateOperatorOptions = (
  fieldSchema: JsonSchema,
  workflowSchema: WorkflowSchema,
  initialOperator?: WorkflowOperatorKey,
) => {
  const schemaType = fieldSchema ? getJsonSchemaType(fieldSchema) : null;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const fieldType = fieldSchema?.fieldType || JsonSchemaTypeToFieldType[schemaType];

  const initialOperatorOption = initialOperator
    ? {
        value: initialOperator,
        label: getOperatorDisplayName(initialOperator),
      }
    : null;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const supportedOperators = workflowSchema.operators[fieldType]?.length
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      workflowSchema.operators[fieldType]
    : [];

  if (supportedOperators.length === 0) {
    return initialOperatorOption ? [initialOperatorOption] : [];
  }

  const supportedOperatorOptions = supportedOperators
    .filter(
      // TODO(@platform): Remove this filter once we support comparisons on complex objects
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (operator) =>
        !('type' in fieldSchema) ||
        fieldSchema.type !== JsonSchemaTypes.Array ||
        !('type' in fieldSchema.items) ||
        fieldSchema.items?.type !== JsonSchemaTypes.Object ||
        SUPPORTED_COMPLEX_ARRAY_OPERATOR.includes(operator),
    )
    .filter(
      // TODO(@platform): Remove this filter once we support fine grained trigger types beyond string, bool, etc
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (operator) =>
        !VERSION_OPERATORS.includes(operator) ||
        SUPPORTED_WORKFLOW_FIELD_KEY_DESCRIPTIONS_FOR_VERSION_OPERATORS.includes(
          fieldSchema.description ?? '',
        ),
    )
    .map((operator: WorkflowOperatorKey) => {
      const operatorOption = {
        value: operator,
        label: getOperatorDisplayName(operator),
      };

      // if operator label matches label for the initial operator, return value matching initial operator
      // some operators like = and == are functionally the same and map to the same label on the frontend
      // we want to return the option matching the initial value so the dropdown can mark the correct label as selected
      if (operatorOption.label === initialOperatorOption?.label) {
        return initialOperatorOption;
      } else {
        return operatorOption;
      }
    });

  // if we don't find a matching operator with the same name, add it so the dropdown is populated with a value
  if (
    initialOperatorOption &&
    !supportedOperatorOptions.some(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (operatorOption) => initialOperatorOption.label === operatorOption.label,
    )
  ) {
    supportedOperatorOptions.push(initialOperatorOption);
  }

  return supportedOperatorOptions;
};

const getFormattedValue = (valueConfig: ConditionalRuleValueConfig): JsonLogicValue => {
  switch (valueConfig.type) {
    case RuleValueTypes.Value:
      return valueConfig.value;
    case RuleValueTypes.Field: {
      const { value, paths } = valueConfig;
      return Array.isArray(value) ? paths : paths[0];
    }
    default:
      assertNever(valueConfig);
  }
};

const isRuleValueConfigValid = (config: ConditionalRuleValueConfig) => {
  if (config == null) {
    return false;
  }

  switch (config.type) {
    case RuleValueTypes.Field:
      return !!config.paths.length && config.paths.every(Boolean);
    case RuleValueTypes.Value:
      return config.value !== '';
  }
};

const Grid = styled.div<{ withLongOperator?: boolean }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 156px 1fr 32px;
  column-gap: ${Spacing[2]};

  ${({ withLongOperator }) => {
    if (withLongOperator) {
      return css`
        transition: ${TransitionDuration.Fast};
        transition-property: grid-template-columns;

        &:has(button:nth-child(2)[aria-expanded='false']:hover),
        &:has(button:nth-child(2)[aria-expanded='false']:focus) {
          grid-template-columns: 1fr 238px 1fr 32px;
        }
      `;
    }
  }}
`;

const InnerGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CenteredIcon = styled.div`
  display: block;
  margin-top: auto;
  margin-bottom: auto;
`;

const StyledLabel = styled(Label)`
  padding: ${Spacing[2]} 0;
`;

interface BaseConditionalRowProps {
  workflowSchema: WorkflowSchema;
  andRule: ConditionalRule;
  orIndex: number;
  andIndex: number;
  jsonSchema: JsonSchema;
  tokensToDisplayValue: Record<string, string>;
  updateConditionalRule: (conditional: ConditionalRule) => void;
  deleteConditional: () => void;
}

const BaseConditionalRow: FC<BaseConditionalRowProps> = ({
  workflowSchema,
  andRule,
  orIndex,
  andIndex,
  jsonSchema,
  tokensToDisplayValue,
  updateConditionalRule,
  deleteConditional,
}) => {
  const schemaForFieldPath = useSchemaForFieldPath();

  const {
    operator: initialOperator,
    valueLeftConfig,
    valueRightConfig,
  } = getConditionalRuleParts(andRule);
  const [leftConfig, setLeftConfig] = useState<RuleValueConfigField>(
    valueLeftConfig || DEFAULT_LEFT_CONFIG,
  );
  const [rightConfig, setRightConfig] = useState<ConditionalRuleValueConfig>(
    valueRightConfig || DEFAULT_RIGHT_CONFIG,
  );
  const leftValueFormatted = getFormattedValue(leftConfig) as string;
  const rightValueFormatted = getFormattedValue(rightConfig);

  // left hand side determines schema for whole row
  const [rowSchema, setRowSchema] = useState<JsonSchema | null>(
    schemaForFieldPath(
      valueTransformer(tokensToDisplayValue).getDisplayValue(leftValueFormatted),
      jsonSchema,
    ).schema,
  );
  const [operator, setOperator] = useState(initialOperator);
  const [displayRightHand, setDisplayRightHand] = useState(!hasNoPossibilities(initialOperator));
  const [operatorOptions, setOperatorOptions] = useState(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    generateOperatorOptions(rowSchema, workflowSchema, initialOperator),
  );
  const isMulti = isMultiValue(operator);

  const setLeftType = (type: RuleValueTypes.Field) => {
    const leftValue = buildValueOneSide(type, '');
    setLeftConfig({
      ...(getConditionalRuleValueConfig(leftValue) as RuleValueConfigField),
      type,
    });
  };

  const setLeftValue = (val: JsonLogicValue) => {
    const leftValue = buildValueOneSide(leftConfig.type, val);
    const config = getConditionalRuleValueConfig(leftValue) as RuleValueConfigField;
    setLeftConfig({
      ...config,
      type: leftConfig.type,
    });
    return config;
  };

  const setRightType = (type: ConditionalRuleValueTypes) => {
    const rightValue = buildValueOneSide(type, isMulti ? [] : '');
    setRightConfig({
      ...getConditionalRuleValueConfig(rightValue),
      type,
    } as ConditionalRuleValueConfig);
  };

  const setRightValue = (val: JsonLogicValue) => {
    const rightValue = buildValueOneSide(rightConfig.type, val);
    const config = getConditionalRuleValueConfig(rightValue);
    setRightConfig({
      ...config,
      type: rightConfig.type,
    } as ConditionalRuleValueConfig);
    return config;
  };

  const updateRule = useCallback(
    (
      newLeftConfig: RuleValueConfigField,
      newOperator: WorkflowOperatorKey,
      newRightConfig: ConditionalRuleValueConfig,
    ) => {
      if (hasNoPossibilities(newOperator)) {
        setDisplayRightHand(false);
        if (isRuleValueConfigValid(newLeftConfig) && newOperator != null) {
          updateConditionalRule(buildConditionalRule(newOperator, newLeftConfig.value));
        }
      } else {
        setDisplayRightHand(true);
        if (
          isRuleValueConfigValid(newLeftConfig) &&
          isRuleValueConfigValid(newRightConfig) &&
          newOperator != null
        ) {
          updateConditionalRule(
            buildConditionalRule(newOperator, newLeftConfig.value, newRightConfig.value),
          );
        }
      }
    },
    [leftConfig.type, rightConfig.type, updateConditionalRule],
  );

  const configureRow = useCallback(
    (option: Option) => {
      if (option.value === leftValueFormatted) {
        return;
      }

      const newLeftConfig = setLeftValue(option.value);
      let newRowSchema: JsonSchema | null = null;
      let newRightConfig: ConditionalRuleValueConfig | null = null;
      if (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (option.schema?.type && option.schema.type !== rowSchema?.type) ||
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (option.schema?.fieldType && option.schema.fieldType !== rowSchema?.fieldType)
      ) {
        newRowSchema = option.schema;
        newRightConfig = setRightValue(isMulti ? [] : '');
      } else if (rowSchema != null) {
        newRowSchema = rowSchema;
        newRightConfig = rightConfig;
      }

      if (newRowSchema != null && newRightConfig != null) {
        setRowSchema(option.schema);
        const newOperatorOptions = generateOperatorOptions(option.schema, workflowSchema);
        setOperatorOptions(newOperatorOptions);
        wrappedSetOperator(
          newOperatorOptions[0]?.value,
          newLeftConfig,
          newRightConfig,
          option.schema,
        );
      } else {
        console.error('Something went wrong. Failed to configure conditional row');
      }
    },
    [leftValueFormatted, isMulti, rowSchema],
  );

  const wrappedSetOperator = useCallback(
    (
      newOperator: WorkflowOperatorKey,
      leftConfig: RuleValueConfigField,
      rightConfig: ConditionalRuleValueConfig,
      rowSchema: JsonSchema,
    ) => {
      const operatorToSet =
        newOperator === WorkflowOperatorKey.DoubleEquals ? WorkflowOperatorKey.Equals : newOperator;
      const isMultiPrevious = isMultiValue(operator);
      const isMultiCurrent = isMultiValue(operatorToSet);
      setOperator(operatorToSet);

      if (isMultiCurrent !== isMultiPrevious) {
        const newRightConfig = setRightValue(isMultiCurrent ? [] : '');
        updateRule(leftConfig, operatorToSet, newRightConfig);
      } else {
        updateRule(leftConfig, operatorToSet, rightConfig);
      }

      // reset operator options after changing operator to handle cases of == being mapped to =
      // or deselecting an unsupported operator
      const newOperatorOptions = generateOperatorOptions(rowSchema, workflowSchema, operatorToSet);
      setOperatorOptions(newOperatorOptions);
    },
    [operator, updateRule, workflowSchema],
  );

  // RuleValueTypes.Value
  const debouncedUpdateRule = useMemo(() => debounce(250, updateRule), [updateRule]);
  const updateRuleRightDebounced = useCallback(
    (value: JsonLogicValue) => {
      const newRightConfig = setRightValue(value);
      debouncedUpdateRule(leftConfig, operator, newRightConfig);
    },
    [leftConfig.value, operator, debouncedUpdateRule],
  );

  // RuleValueTypes.Field
  const updateRuleRight = useCallback(
    (data: Option | Option[]) => {
      const values = Array.isArray(data) ? data.map((option: Option) => option.value) : data.value;
      const newRightConfig = setRightValue(values);
      updateRule(leftConfig, operator, newRightConfig);
    },
    [leftConfig.value, operator, updateRule],
  );

  const disallowedFieldPaths = useMemo(() => {
    const extraPaths = Array.isArray(rightValueFormatted) ? rightValueFormatted : [];
    return leftConfig.type === RuleValueTypes.Field
      ? [...extraPaths, leftValueFormatted]
      : extraPaths;
  }, [leftConfig.type, leftValueFormatted, rightValueFormatted]) as string[];

  const isLongOperator = useMemo(
    () => operator && getOperatorDisplayName(operator).length > 18,
    [operator],
  );

  return (
    <div>
      <Grid>
        <InnerGrid>
          <StyledLabel size='sm'>
            {andIndex === 0 ? (orIndex === 0 ? 'Where' : 'Or') : 'And'}
          </StyledLabel>
          <OptionTypeSelect size='md' value={leftConfig.type} onValueChange={setLeftType}>
            {LEFT_HAND_OPTIONS.map((leftHandOption) => (
              <SelectOption key={leftHandOption.value} value={leftHandOption.value}>
                {leftHandOption.value}
              </SelectOption>
            ))}
          </OptionTypeSelect>
        </InnerGrid>
        <div />
        <InnerGrid>
          <div />
          {displayRightHand && (
            <OptionTypeSelect
              id='base-conditional-row__right-type-select'
              size='md'
              triggerProps={{ 'aria-label': 'Select right hand conditional type' }}
              value={rightConfig.type}
              onValueChange={setRightType}
            >
              {RIGHT_HAND_OPTIONS.map((rightHandOption) => (
                <SelectOption key={rightHandOption.value} value={rightHandOption.value}>
                  {rightHandOption.value}
                </SelectOption>
              ))}
            </OptionTypeSelect>
          )}
        </InnerGrid>
        <div /> {/* fake delete button column */}
      </Grid>
      <Spacer height={1} />
      <Grid withLongOperator={isLongOperator}>
        <SideInput
          id='base-conditional-row__left-field-select'
          ariaLabel='Select left JSON schema field'
          configValue={leftValueFormatted}
          configType={leftConfig.type}
          jsonSchema={jsonSchema}
          tokensToDisplayValue={tokensToDisplayValue}
          onConfigValueChange={setLeftValue}
          onFieldSelect={configureRow}
        />
        <Select
          disabled={!operatorOptions?.length}
          id='base-conditional-row__operator'
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          key={`${orIndex}-${andIndex}-${(operatorOptions ?? []).map((o) => o.value).join('-')}`}
          placeholder='Select operator'
          triggerProps={{ 'aria-label': 'Select operator' }}
          value={operator}
          onValueChange={(newOperator: WorkflowOperatorKey) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            wrappedSetOperator(newOperator, leftConfig, rightConfig, rowSchema);
          }}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {(operatorOptions ?? []).map(({ label, value }) => (
            <SelectOption key={value} value={value}>
              {label}
            </SelectOption>
          ))}
        </Select>
        {displayRightHand && (
          <SideInput
            id='base-conditional-row__right-field-select'
            ariaLabel='Select right JSON schema field'
            configValue={rightValueFormatted}
            configType={rightConfig.type}
            disallowedFieldPaths={disallowedFieldPaths}
            fieldSchema={rowSchema || undefined}
            jsonSchema={jsonSchema}
            operator={operator}
            tokensToDisplayValue={tokensToDisplayValue}
            setFieldValue={updateRuleRightDebounced}
            onConfigValueChange={setRightValue}
            onFieldSelect={updateRuleRight}
          />
        )}
        <CenteredIcon>
          <Button
            id='multi-input__value-remove'
            variant='basic'
            width='icon'
            onClick={deleteConditional}
            aria-label={`Delete conditional rule ${andIndex} in group ${orIndex}`}
          >
            <TrashIcon />
          </Button>
        </CenteredIcon>
      </Grid>
    </div>
  );
};

export default BaseConditionalRow;
